// SectionManager — detect, rename, reorder and remove top-level sections
// inside module artifacts from the Visual editor.
//
// Three artifact patterns are supported:
//  • "lesson"   — stepper modules (section.lesson[data-lesson] + a script
//                 `var LESSONS = [{n,t},...]` that builds the nav rail).
//                 Ops also patch the LESSONS array, renumber labels and
//                 data-lesson attrs, sync the live nav and the "N Lessons"
//                 count text.
//  • "anchored" — section[id] blocks with a table-of-contents of a[href="#id"].
//  • "block"    — generic top-level blocks identified by their h1/h2 heading.
//
// All mutations run on the LIVE editor DOM, so they participate in the
// editor's undo history and persist through the stamped save pipeline
// (element text/order is taken live; attribute changes are marked with
// data-uvc-edited so they survive pristine-attribute restoration).
import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trash2, Pencil, ChevronUp, ChevronDown, Check, X, ListTree, RefreshCw } from "lucide-react";

const markEdited = (el) => { if (el && el.nodeType === 1) el.setAttribute("data-uvc-edited", "1"); };

function headingOf(el) {
  return el.querySelector("h1, h2, h3");
}
function headingText(el) {
  const h = headingOf(el);
  return h ? (h.textContent || "").trim().replace(/\s+/g, " ").slice(0, 80) : "";
}

// ---------- lesson (stepper) helpers ----------

function findLessonsScript(root) {
  return [...root.querySelectorAll("script")].find((s) => /var\s+LESSONS\s*=/.test(s.textContent || ""));
}

function parseLessonsArray(root) {
  const s = findLessonsScript(root);
  const m = s && (s.textContent || "").match(/var\s+LESSONS\s*=\s*\[([\s\S]*?)\]\s*;/);
  if (!m) return null;
  const raws = m[1].match(/\{[^{}]*\}/g) || [];
  return raws.map((raw) => ({
    raw,
    n: (raw.match(/n\s*:\s*"((?:[^"\\]|\\.)*)"/) || [])[1] ?? "",
    t: (raw.match(/t\s*:\s*"((?:[^"\\]|\\.)*)"/) || [])[1] ?? "",
  }));
}

// Regenerate sequential number labels when every label is purely numeric
// (preserves zero-padding and a trailing dot, e.g. "01".."09" or "1.".."9.").
function renumberLabels(labels) {
  const numeric = labels.every((l) => /^\d+\.?$/.test(l || ""));
  if (!numeric || labels.length === 0) return labels;
  const pad = (labels[0].match(/^\d+/) || ["1"])[0].length;
  const dot = labels[0].endsWith(".") ? "." : "";
  return labels.map((_, i) => String(i + 1).padStart(pad, "0") + dot);
}

function writeLessonsArray(root, entries) {
  const s = findLessonsScript(root);
  const txt = s && (s.textContent || "");
  const m = txt && txt.match(/var\s+LESSONS\s*=\s*\[[\s\S]*?\]\s*;/);
  if (!m) return;
  const ns = renumberLabels(entries.map((e) => e.n));
  const body = entries
    .map((e, i) => {
      let raw = e.raw;
      raw = raw.replace(/n\s*:\s*"((?:[^"\\]|\\.)*)"/, () => `n:"${ns[i].replace(/"/g, '\\"')}"`);
      raw = raw.replace(/t\s*:\s*"((?:[^"\\]|\\.)*)"/, () => `t:"${(e.t || "").replace(/"/g, '\\"')}"`);
      return raw;
    })
    .join(",\n    ");
  s.textContent = txt.replace(m[0], `var LESSONS = [\n    ${body}\n  ];`);
  return ns;
}

function lessonSections(root) {
  return [...root.querySelectorAll("section.lesson[data-lesson]")];
}

// Keep data-lesson attrs sequential and exactly the first lesson active.
function normalizeLessons(root) {
  const secs = lessonSections(root);
  secs.forEach((sec, i) => {
    if (sec.getAttribute("data-lesson") !== String(i)) {
      sec.setAttribute("data-lesson", String(i));
      markEdited(sec);
    }
    const want = i === 0;
    if (sec.classList.contains("is-active") !== want) {
      sec.classList.toggle("is-active", want);
      markEdited(sec);
    }
  });
}

// Sync the (script-built, ephemeral) nav rail + mobile select for instant
// visual feedback in the editor. These nodes are unstamped and never saved.
function syncLessonNav(root, entries) {
  const ns = renumberLabels(entries.map((e) => e.n));
  const btns = [...root.querySelectorAll("#railList .lesson-btn")];
  const opts = [...root.querySelectorAll("#mobileSel option")];
  btns.forEach((b, i) => {
    if (i >= entries.length) { b.remove(); return; }
    const ix = b.querySelector(".ix");
    const nm = b.querySelector(".nm");
    if (ix && /^\d+\.?$/.test((ix.textContent || "").trim())) ix.textContent = ns[i];
    if (nm) nm.textContent = entries[i].t;
  });
  opts.forEach((o, i) => {
    if (i >= entries.length) { o.remove(); return; }
    o.textContent = `${i + 1}. ${entries[i].t}`;
  });
}

// Update small "N Lessons" count labels (e.g. <span><b>10</b> Lessons</span>).
function updateLessonCount(root, oldCount, newCount) {
  if (oldCount === newCount) return;
  const re = new RegExp(`^\\s*${oldCount}\\s+Lessons?\\s*$`, "i");
  [...root.querySelectorAll("*")]
    .filter((el) => el.children.length <= 1 && re.test(el.textContent || ""))
    .forEach((el) => {
      const walker = (el.ownerDocument || document).createTreeWalker(el, NodeFilter.SHOW_TEXT);
      let t;
      while ((t = walker.nextNode())) {
        if (/\d+/.test(t.data)) { t.data = t.data.replace(/\d+/, String(newCount)); break; }
      }
    });
}

// ---------- detection ----------

// Find the top-level block a heading belongs to: climb until a sibling
// contains one of the OTHER headings (sections are siblings of each other).
function topBlockFor(h, root, allHeads) {
  let a = h;
  while (a.parentElement && a.parentElement !== root) {
    const p = a.parentElement;
    const siblingHasOtherHead = [...p.children].some(
      (sib) => sib !== a && allHeads.some((hh) => hh !== h && sib.contains(hh))
    );
    if (siblingHasOtherHead) return a;
    a = p;
  }
  return a.parentElement === root ? a : null;
}

export function detectSections(root) {
  if (!root) return [];
  // A: lesson stepper
  const lessons = lessonSections(root);
  if (lessons.length >= 2) {
    const entries = parseLessonsArray(root);
    return lessons.map((el, i) => ({
      kind: "lesson",
      el,
      label: entries?.[i]?.t || headingText(el) || `Lesson ${i + 1}`,
    }));
  }
  // B: anchored sections
  const anchored = [...root.querySelectorAll("section[id]")].filter((s) => headingText(s));
  if (anchored.length >= 2) {
    return anchored.map((el) => ({ kind: "anchored", el, label: headingText(el) || `#${el.id}` }));
  }
  // C: generic heading blocks
  const heads = [...root.querySelectorAll("h1, h2")].filter((h) => (h.textContent || "").trim());
  const seen = new Set();
  const blocks = [];
  heads.forEach((h) => {
    const block = topBlockFor(h, root, heads);
    if (block && block !== root && !seen.has(block)) {
      seen.add(block);
      blocks.push({ kind: "block", el: block, label: (h.textContent || "").trim().replace(/\s+/g, " ").slice(0, 80) });
    }
  });
  return blocks.length >= 2 ? blocks : [];
}

// ---------- operations ----------

export function removeSection(root, sections, idx) {
  const { kind, el } = sections[idx];
  const parent = el.parentElement;
  if (kind === "lesson") {
    const entries = parseLessonsArray(root) || [];
    el.remove();
    markEdited(parent);
    if (entries[idx]) {
      const oldCount = entries.length;
      entries.splice(idx, 1);
      writeLessonsArray(root, entries);
      updateLessonCount(root, oldCount, entries.length);
      syncLessonNav(root, entries);
    }
    normalizeLessons(root);
  } else if (kind === "anchored") {
    const id = el.id;
    el.remove();
    markEdited(parent);
    [...root.querySelectorAll(`a[href="#${CSS.escape(id)}"]`)].forEach((a) => {
      const item = a.closest("li") || a;
      markEdited(item.parentElement);
      item.remove();
    });
  } else {
    el.remove();
    markEdited(parent);
  }
}

export function renameSection(root, sections, idx, newLabel) {
  const { kind, el, label } = sections[idx];
  const clean = (newLabel || "").trim();
  if (!clean) return;
  if (kind === "lesson") {
    const entries = parseLessonsArray(root) || [];
    if (entries[idx]) {
      entries[idx] = { ...entries[idx], t: clean };
      writeLessonsArray(root, entries);
      syncLessonNav(root, entries);
    }
    // Update the in-content heading only when it mirrors the nav label.
    const h = headingOf(el);
    if (h && (h.textContent || "").trim().replace(/\s+/g, " ") === label) {
      h.textContent = clean;
      markEdited(h);
    }
  } else {
    const h = headingOf(el);
    if (h) {
      h.textContent = clean;
      markEdited(h);
    }
    if (kind === "anchored") {
      [...root.querySelectorAll(`a[href="#${CSS.escape(el.id)}"]`)].forEach((a) => {
        if ((a.textContent || "").trim().replace(/\s+/g, " ") === label) {
          a.textContent = clean;
          markEdited(a);
        }
      });
    }
  }
}

export function moveSection(root, sections, idx, dir) {
  const to = idx + dir;
  if (to < 0 || to >= sections.length) return;
  const { kind, el } = sections[idx];
  const target = sections[to].el;
  if (dir < 0) target.before(el);
  else target.after(el);

  if (kind === "lesson") {
    const entries = parseLessonsArray(root) || [];
    if (entries[idx] && entries[to]) {
      const [moved] = entries.splice(idx, 1);
      entries.splice(to, 0, moved);
      writeLessonsArray(root, entries);
      syncLessonNav(root, entries);
    }
    normalizeLessons(root);
  } else if (kind === "anchored") {
    // Reorder the matching TOC items too.
    const itemFor = (sec) => {
      const a = root.querySelector(`a[href="#${CSS.escape(sec.id)}"]`);
      return a ? a.closest("li") || a : null;
    };
    const a1 = itemFor(el);
    const a2 = itemFor(target);
    if (a1 && a2) {
      if (dir < 0) a2.before(a1);
      else a2.after(a1);
    }
  }
}

// ---------- panel UI ----------

export function SectionsPanel({ getRoot, onBeforeChange, onAfterChange }) {
  const [sections, setSections] = React.useState([]);
  const [renaming, setRenaming] = React.useState(null);
  const [renameVal, setRenameVal] = React.useState("");
  const [armed, setArmed] = React.useState(null);

  const refresh = React.useCallback(() => {
    setSections(detectSections(getRoot()));
    setRenaming(null);
    setArmed(null);
  }, [getRoot]);

  React.useEffect(() => { refresh(); }, [refresh]);

  const exec = (fn) => {
    const root = getRoot();
    if (!root) return;
    onBeforeChange();
    fn(root);
    onAfterChange();
    refresh();
  };

  if (!sections.length) {
    return (
      <div className="p-4 text-xs text-slate-500" data-testid="sections-panel-empty">
        No sections detected in this artifact.
      </div>
    );
  }

  return (
    <div data-testid="sections-panel">
      <div className="flex items-center justify-between px-3 pt-2.5 pb-1.5">
        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
          Sections · {sections.length}
        </span>
        <button
          onClick={refresh}
          className="text-slate-400 hover:text-slate-600 transition-colors"
          title="Refresh list"
          data-testid="sections-refresh"
        >
          <RefreshCw size={12} />
        </button>
      </div>
      <div className="max-h-80 overflow-y-auto px-1.5 pb-1">
        {sections.map((s, i) => (
          <div
            key={i}
            className="group flex items-center gap-1 px-1.5 py-1 rounded hover:bg-slate-50"
            data-testid={`section-row-${i}`}
          >
            <span className="text-[10px] font-mono text-slate-400 w-5 shrink-0 text-right">{i + 1}.</span>
            {renaming === i ? (
              <>
                <Input
                  value={renameVal}
                  onChange={(e) => setRenameVal(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") exec((root) => renameSection(root, sections, i, renameVal));
                    if (e.key === "Escape") setRenaming(null);
                  }}
                  autoFocus
                  className="h-6 text-xs flex-1"
                  data-testid={`section-rename-input-${i}`}
                />
                <button
                  onClick={() => exec((root) => renameSection(root, sections, i, renameVal))}
                  className="p-1 text-emerald-600 hover:bg-emerald-50 rounded"
                  title="Apply rename"
                  data-testid={`section-rename-apply-${i}`}
                >
                  <Check size={13} />
                </button>
                <button
                  onClick={() => setRenaming(null)}
                  className="p-1 text-slate-400 hover:bg-slate-100 rounded"
                  title="Cancel"
                >
                  <X size={13} />
                </button>
              </>
            ) : (
              <>
                <span className="text-xs text-slate-700 truncate flex-1" title={s.label}>{s.label}</span>
                <div className="flex items-center gap-0.5 opacity-30 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => exec((root) => moveSection(root, sections, i, -1))}
                    disabled={i === 0}
                    className="p-1 text-slate-500 hover:bg-slate-200 rounded disabled:opacity-25"
                    title="Move up"
                    data-testid={`section-move-up-${i}`}
                  >
                    <ChevronUp size={13} />
                  </button>
                  <button
                    onClick={() => exec((root) => moveSection(root, sections, i, 1))}
                    disabled={i === sections.length - 1}
                    className="p-1 text-slate-500 hover:bg-slate-200 rounded disabled:opacity-25"
                    title="Move down"
                    data-testid={`section-move-down-${i}`}
                  >
                    <ChevronDown size={13} />
                  </button>
                  <button
                    onClick={() => { setRenaming(i); setRenameVal(s.label); setArmed(null); }}
                    className="p-1 text-slate-500 hover:bg-slate-200 rounded"
                    title="Rename section"
                    data-testid={`section-rename-${i}`}
                  >
                    <Pencil size={12} />
                  </button>
                  {armed === i ? (
                    <button
                      onClick={() => exec((root) => removeSection(root, sections, i))}
                      className="px-1.5 py-0.5 text-[10px] font-bold bg-[#E53935] text-white rounded"
                      title="Click again to permanently remove this section"
                      data-testid={`section-remove-confirm-${i}`}
                    >
                      Sure?
                    </button>
                  ) : (
                    <button
                      onClick={() => setArmed(i)}
                      className="p-1 text-[#E53935] hover:bg-red-50 rounded"
                      title="Remove this section entirely"
                      data-testid={`section-remove-${i}`}
                    >
                      <Trash2 size={12} />
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        ))}
      </div>
      <div className="px-3 py-2 text-[10px] text-slate-500 border-t border-slate-100 leading-snug">
        Changes are applied to the module when you click <b>Save</b>. Use Ctrl/Cmd+Z to undo before saving.
      </div>
    </div>
  );
}

export function SectionsTriggerIcon(props) {
  return <ListTree {...props} />;
}
